import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  SearchBox,
  StackItem,
} from "@fluentui/react";
import { Selection, SelectionMode } from "@fluentui/react/lib/DetailsList";
import { Text } from "@fluentui/react/lib/Text";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import _ from "lodash";
import { useRefresh } from "hooks/useRefresh";
import { IPublishedTemplate } from "models/templates";
import { getCookieCache, setCookieCache } from "helpers/cookieCache";
import { useGetPolicyPublishedTemplatesQuery } from "services/template";
import { usePolicyEditStructureNodeManager } from "./PolicyEditStructureNodeManager";
import TemplateList from "./TemplateList";

const TEMPLATES_COOKIE_KEY = "policyPublishedTemplates";
const TEMPLATES_COOKIE_MAX_AGE = 3600; // 1 hour

const addTemplatePanelClass = mergeStyleSets({
  templateContent: {
    flex: 1,
    overflow: "auto",
  },
  addTemplateText: {
    fontSize: "12px",
  },
  templatePanel: {
    height: "100%",
    zIndex: 1,
  },
});

function AddTemplatePanel({ closePanel }: { closePanel: () => void }) {
  const cachedTemplates = useMemo(() => getCookieCache<IPublishedTemplate[]>(TEMPLATES_COOKIE_KEY), []);
  const { data: fetchedTemplates, isLoading: isFetching, error } = useGetPolicyPublishedTemplatesQuery(undefined, {
    skip: !!cachedTemplates,
  });

  const templates = cachedTemplates ?? fetchedTemplates ?? [];
  const isLoading = !cachedTemplates && isFetching;

  // Cache templates in cookie once fetched from API
  useEffect(() => {
    if (fetchedTemplates && fetchedTemplates.length > 0) {
      setCookieCache(TEMPLATES_COOKIE_KEY, fetchedTemplates, TEMPLATES_COOKIE_MAX_AGE);
    }
  }, [fetchedTemplates]);

  const { addTemplateToPolicy } = usePolicyEditStructureNodeManager();

  const filterTextRef = useRef<string>("");
  const [filterUpdatedToken, triggerFilterUpdate] = useRefresh();
  const [filteredTemplates, setFilteredTemplates] = useState<IPublishedTemplate[]>([]);

  const selection = useMemo(
    () =>
      new Selection({
        selectionMode: SelectionMode.single,
        onSelectionChanged: () => {
          // Selection change handling can be added here if needed
        },
      }),
    []
  );

  const getSelectedTemplates = useCallback(() => {
    const selectedItems = selection.getSelection() as IPublishedTemplate[];
    return _.cloneDeep(selectedItems);
  }, [selection]);

  const handleTemplateSelect = useCallback((template: IPublishedTemplate | null) => {
    if (template && addTemplateToPolicy) {
      addTemplateToPolicy(template);
      closePanel();
    }
  }, [addTemplateToPolicy, closePanel]);

  useEffect(() => {
    if (templates.length > 0) {
      const selectedTemplateIds = getSelectedTemplates().map((t) => t.key);
      let filteredData = filterTextRef.current
        ? templates.filter(
            (i) =>
              i.name
                .toLowerCase()
                .indexOf(filterTextRef.current.toLowerCase()) > -1
          )
        : templates;
      filteredData = filteredData.filter((t) => !selectedTemplateIds.includes(t.key));
      filteredData = [...getSelectedTemplates(), ...filteredData];
      setFilteredTemplates(filteredData);
    } else {
      setFilteredTemplates([]);
    }
  }, [templates, filterUpdatedToken, getSelectedTemplates, setFilteredTemplates]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '500px',
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.3)',
        zIndex: 10000,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
      data-automation-id="policyEdit-structure-addTemplate-panel"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontWeight: 'bold' }}>Add template</h2>
        <button 
          type="button"
          onClick={closePanel}
          style={{ 
            background: 'none', 
            border: 'none', 
            fontSize: '20px', 
            cursor: 'pointer' 
          }}
        >
          ×
        </button>
      </div>
      
      <Text className={addTemplatePanelClass.addTemplateText}>
        Drag a template onto the canvas to specifically to select the templates where this policy is applicable.
      </Text>

      <StackItem style={{ padding: "0px", width: "300px", marginTop: "20px" }}>
        <SearchBox
          data-automation-id="addtemplatesearchbox"
          placeholder="Filter by name"
          width="300px"
          onChange={(event) => {
            const text = event?.currentTarget.value as string;
            filterTextRef.current = text;
            triggerFilterUpdate();
          }}
        />
      </StackItem>

      <div className={addTemplatePanelClass.templateContent}>
        {(() => {
          if (isLoading) {
            return <div style={{ textAlign: 'center', padding: '20px' }}>Loading templates...</div>;
          }
          if (error) {
            const isAuthError = error && typeof error === 'object' && 'status' in error && error.status === 401;
            return (
              <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                <div>Error loading templates</div>
                {isAuthError && (
                  <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                    Please refresh the page to re-authenticate
                  </div>
                )}
              </div>
            );
          }
          return (
            <TemplateList
              templates={filteredTemplates || []}
              onTemplateSelect={handleTemplateSelect}
            />
          );
        })()
        }
      </div>
    </div>
  );
}

export default AddTemplatePanel;